/**
 * LoadMoreSentinel — F-006 Phase 5 (IMPL-P5-005).
 *
 * Invisible sentinel observed via `IntersectionObserver`. When it enters the
 * viewport AND there's a next page AND we're not already fetching, calls
 * `fetchNextPage()` from the archivio provider.
 *
 * Falls back to a "Carica altri" button when IntersectionObserver isn't
 * available (very old browsers / jsdom test env).
 *
 * Accessibility: `aria-live="polite"` so screen readers announce "Caricamento
 * altre pratiche…" when a new page is being fetched.
 */
import { useEffect, useRef } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useArchivio } from '../hooks/useArchivio';

export function LoadMoreSentinel() {
  const { fetchNextPage, hasNextPage, isFetchingNextPage } = useArchivio();
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!hasNextPage) return;
    if (typeof IntersectionObserver === 'undefined') return; // jsdom fallback
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            fetchNextPage();
            break;
          }
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage]);

  if (!hasNextPage && !isFetchingNextPage) {
    return null;
  }

  return (
    <div
      ref={ref}
      aria-live="polite"
      className="flex items-center justify-center py-6"
    >
      {isFetchingNextPage ? (
        <div className="inline-flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          <span>Caricamento altre pratiche…</span>
        </div>
      ) : hasNextPage ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => fetchNextPage()}
          aria-label="Carica altre pratiche"
        >
          Carica altre
        </Button>
      ) : null}
    </div>
  );
}
